'use client';

import type { Row } from 'src/lib/firebase/firestore-kit';

import { useRef, useMemo, useState, useEffect, useCallback, createContext } from 'react';

import { getPlanAccessContext } from 'src/utils/plan/plan-access';
import { getPostMemoKey } from 'src/utils/post-memo/post-memo-key';
import { isPostMemoBlockedByPlan, POST_MEMO_PLAN_BLOCKED_MESSAGE } from 'src/utils/post-memo/post-memo-plan-gate';

import { fsCall, fsSubscribe } from 'src/actions/firestore/dispatch';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

/** 메모 문서 한 건 — `postMemo.byWorkspace` 가 주는 행 그대로 */
export type PostMemoDoc = Row & {
  postKey?: string;
  memo?: string;
  updatedBy?: string | null;
  updatedAt?: unknown;
};

export interface PostMemoContextValue {
  /** 워크스페이스·로그인이 갖춰졌는가 — 없으면 저장이 막힌다 */
  canUse: boolean;
  /** 지금 워크스페이스 플랜이 메모를 막는가(체험 플랜) — 셀은 업그레이드 팝오버로 안내한다 */
  planBlocked: boolean;
  /** 포스트 → 메모 문서. 없으면 `null` */
  getMemo: (post: unknown) => PostMemoDoc | null;
  /** 저장. 빈 본문은 삭제다. 실패는 throw(문구가 토스트 계약) */
  setMemo: (post: unknown, memo: string) => Promise<void>;
}

export const PostMemoContext = createContext<PostMemoContextValue | null>(null);

/** user 전체가 아니라 workspaceId·uid 둘만 본다 — 참조가 바뀌어도 값이 같으면 같은 객체 */
function usePostMemoAuth() {
  const { user } = useAuthContext();
  return useMemo(
    () => ({ workspaceId: String(user?.lastWorkspace ?? '').trim(), userId: String(user?.uid ?? '').trim() }),
    [user?.lastWorkspace, user?.uid]
  );
}

/** 지금 워크스페이스의 플랜이 메모를 막는가 — 플랜 판정은 다른 게이트와 같은 자리(`plan-access`)에서 읽는다 */
function usePostMemoPlanGate(): boolean {
  const { user } = useAuthContext();
  return useMemo(() => isPostMemoBlockedByPlan(getPlanAccessContext(user).planId), [user]);
}

/**
 * 워크스페이스의 **포스트 메모** 상태 — 북마크와 같은 자리(앱 레이아웃 · AuthProvider 아래)에 산다.
 *
 * 저장 위치가 `CatchMomentsWorkspaceProfiles/{ws}/postMemos` 라 같은 워크스페이스의 어느 표에서
 * 봐도 같은 메모가 보이고, 다른 워크스페이스에서는 보이지 않는다.
 * 키는 제외 등록과 같은 규칙(`getPostMemoKey`)이다.
 */
export function PostMemoProvider({ children }: { children: React.ReactNode }) {
  const { workspaceId, userId } = usePostMemoAuth();
  const planBlocked = usePostMemoPlanGate();
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;

  const [memoByKey, setMemoByKey] = useState<Map<string, PostMemoDoc>>(() => new Map());

  useEffect(() => {
    if (!workspaceId) {
      setMemoByKey(new Map());
      return undefined;
    }
    const unsub = fsSubscribe('postMemo.byWorkspace', { workspaceId }, {
      onChange: (rows) => {
        // 워크스페이스가 바뀐 뒤 늦게 온 스냅샷은 버린다
        if (workspaceIdRef.current !== workspaceId) return;
        const next = new Map<string, PostMemoDoc>();
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const key = String(row?.postKey ?? '').trim();
          if (key) next.set(key, row as PostMemoDoc);
        });
        setMemoByKey(next);
      },
    });
    return () => {
      unsub();
    };
  }, [workspaceId]);

  const getMemo = useCallback(
    (post: unknown) => {
      const key = getPostMemoKey(post);
      return key ? memoByKey.get(key) ?? null : null;
    },
    [memoByKey]
  );

  const setMemo = useCallback(
    async (post: unknown, memo: string) => {
      if (!workspaceId) throw new Error('워크스페이스가 필요합니다.');
      if (!userId) throw new Error('로그인이 필요합니다.');
      if (planBlocked) throw new Error(POST_MEMO_PLAN_BLOCKED_MESSAGE);
      const postKey = getPostMemoKey(post);
      if (!postKey) throw new Error('이 포스트는 식별할 수 없어 메모를 남길 수 없습니다.');
      await fsCall('postMemo.set', { workspaceId, postKey, memo: String(memo ?? ''), updatedBy: userId });
    },
    [workspaceId, userId, planBlocked]
  );

  const value = useMemo<PostMemoContextValue>(
    () => ({ canUse: Boolean(workspaceId && userId), planBlocked, getMemo, setMemo }),
    [workspaceId, userId, planBlocked, getMemo, setMemo]
  );

  return <PostMemoContext.Provider value={value}>{children}</PostMemoContext.Provider>;
}
